import { HanamiElement, define, uniqueId } from '../core/base';
import { getFocusable } from '../core/focus';
import { applyPosition, autoUpdate, computePosition, hidePopover, showPopover, supportsPopover, type Placement } from '../core/position';

/**
 * Infobulle accessible (aria-describedby), au survol et au focus clavier.
 *
 *   <hn-tooltip content="Copier le lien"><button class="hn-btn hn-btn--icon">…</button></hn-tooltip>
 *
 * Attributs : content, placement (défaut top), delay (ms, défaut 250), disabled, open.
 * Le texte peut aussi venir d'un enfant <template slot="content"> (HTML riche).
 */
export class HnTooltip extends HanamiElement {
  static readonly tagName = 'hn-tooltip';
  static get observedAttributes() {
    return ['content', 'placement', 'open', 'disabled'];
  }

  private tip?: HTMLElement;
  private showTimer?: ReturnType<typeof setTimeout>;
  private hideTimer?: ReturnType<typeof setTimeout>;
  private stopAutoUpdate?: () => void;
  private describedTarget?: HTMLElement;
  private observer?: MutationObserver;

  get content(): string {
    return this.getAttribute('content') ?? '';
  }
  set content(value: string) {
    this.setAttribute('content', value);
  }

  get placement(): Placement {
    return (this.getAttribute('placement') as Placement) || 'top';
  }
  set placement(value: Placement) {
    this.setAttribute('placement', value);
  }

  get open(): boolean {
    return this.getBool('open');
  }
  set open(value: boolean) {
    this.setBool('open', value);
  }

  /** Élément décrit par l'infobulle : le premier élément focusable du contenu. */
  get target(): HTMLElement | null {
    const first = this.firstElementChild as HTMLElement | null;
    if (!first || first.matches('template')) return null;
    return first.matches('button, a[href], input, select, textarea, [tabindex]') ? first : (getFocusable(first)[0] ?? first);
  }

  connectedCallback(): void {
    this.upgradeProperties('content', 'placement', 'open');
    this.addEventListener('pointerenter', this.onEnter);
    this.addEventListener('pointerleave', this.onLeave);
    this.addEventListener('focusin', this.onFocusIn);
    this.addEventListener('focusout', this.onLeave);
    this.addEventListener('click', this.onClick);
    // Créée tout de suite pour que aria-describedby soit en place avant le premier focus.
    this.ensureTip();
    this.observer = new MutationObserver(() => {
      this.renderContent();
      this.link();
    });
    this.observer.observe(this, { childList: true });
    if (this.open) this.reveal();
  }

  disconnectedCallback(): void {
    this.removeEventListener('pointerenter', this.onEnter);
    this.removeEventListener('pointerleave', this.onLeave);
    this.removeEventListener('focusin', this.onFocusIn);
    this.removeEventListener('focusout', this.onLeave);
    this.removeEventListener('click', this.onClick);
    this.observer?.disconnect();
    this.conceal();
    this.unlink();
    this.tip?.remove();
    this.tip = undefined;
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (!this.isConnected || oldValue === newValue) return;
    if (name === 'content') {
      this.renderContent();
      if (this.tip?.hasAttribute('data-open')) this.reposition();
    } else if (name === 'open') {
      if (this.open) this.reveal();
      else this.conceal();
    } else if (name === 'disabled' && this.getBool('disabled')) {
      this.conceal();
    } else if (name === 'placement' && this.tip?.hasAttribute('data-open')) {
      this.reposition();
    }
  }

  show(): void {
    this.open = true;
  }

  hide(): void {
    this.open = false;
  }

  private ensureTip(): HTMLElement {
    if (this.tip) return this.tip;
    const tip = document.createElement('div');
    tip.className = 'hn-tooltip';
    tip.id = uniqueId('hn-tooltip');
    tip.setAttribute('role', 'tooltip');
    if (supportsPopover) tip.setAttribute('popover', 'manual');
    tip.addEventListener('pointerenter', () => clearTimeout(this.hideTimer));
    tip.addEventListener('pointerleave', this.onLeave);
    document.body.append(tip);
    this.tip = tip;
    this.renderContent();
    this.link();
    return tip;
  }

  private renderContent(): void {
    if (!this.tip) return;
    const template = this.querySelector<HTMLTemplateElement>(':scope > template[slot="content"]');
    if (template) this.tip.replaceChildren(template.content.cloneNode(true));
    else this.tip.textContent = this.content;
  }

  private link(): void {
    const target = this.target;
    if (!target || !this.tip) return;
    if (this.describedTarget && this.describedTarget !== target) this.unlink();
    const ids = new Set((target.getAttribute('aria-describedby') ?? '').split(/\s+/).filter(Boolean));
    ids.add(this.tip.id);
    target.setAttribute('aria-describedby', [...ids].join(' '));
    this.describedTarget = target;
  }

  private unlink(): void {
    const target = this.describedTarget;
    if (!target || !this.tip) return;
    const ids = (target.getAttribute('aria-describedby') ?? '').split(/\s+/).filter(id => id && id !== this.tip!.id);
    if (ids.length) target.setAttribute('aria-describedby', ids.join(' '));
    else target.removeAttribute('aria-describedby');
    this.describedTarget = undefined;
  }

  private reposition = (): void => {
    const tip = this.tip;
    const anchor = this.target ?? this;
    if (!tip) return;
    const pos = computePosition(anchor, tip, { placement: this.placement, offset: 10 });
    applyPosition(tip, pos);
    tip.style.setProperty('--hn-tooltip-arrow-x', `${Math.min(Math.max(pos.arrowX, 12), tip.offsetWidth - 12)}px`);
    tip.style.setProperty('--hn-tooltip-arrow-y', `${Math.min(Math.max(pos.arrowY, 10), tip.offsetHeight - 10)}px`);
  };

  private reveal(): void {
    if (this.getBool('disabled')) return;
    const tip = this.ensureTip();
    if (!tip.textContent?.trim() && !tip.childElementCount) return;
    clearTimeout(this.hideTimer);
    showPopover(tip);
    this.reposition();
    this.stopAutoUpdate?.();
    this.stopAutoUpdate = autoUpdate(this.target ?? this, tip, this.reposition);
    document.addEventListener('keydown', this.onEscape, true);
  }

  private conceal(): void {
    clearTimeout(this.showTimer);
    this.stopAutoUpdate?.();
    this.stopAutoUpdate = undefined;
    document.removeEventListener('keydown', this.onEscape, true);
    if (this.tip) hidePopover(this.tip);
  }

  private onEnter = (): void => {
    clearTimeout(this.hideTimer);
    clearTimeout(this.showTimer);
    this.showTimer = setTimeout(() => this.reveal(), this.getNumber('delay', 250));
  };

  private onFocusIn = (event: FocusEvent): void => {
    if ((event.target as Element).matches?.(':focus-visible')) {
      clearTimeout(this.hideTimer);
      this.reveal();
    }
  };

  private onLeave = (event: Event): void => {
    const next = (event as PointerEvent).relatedTarget as Node | null;
    if (next && (this.contains(next) || this.tip?.contains(next))) return;
    clearTimeout(this.showTimer);
    if (this.open) return;
    this.hideTimer = setTimeout(() => this.conceal(), 120);
  };

  private onClick = (): void => {
    if (!this.open) this.conceal();
  };

  private onEscape = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && !this.open) this.conceal();
  };
}

define(HnTooltip.tagName, HnTooltip);
